/**
 * Route-aware SEO metadata.
 *
 * Only public, evergreen pages are indexable: `/`, `/game-rules`, `/toc`.
 * Invitation links (`/join/:roomID`) are private, short-lived and unique per
 * table, so they are `noindex` with no canonical. Every other route (auth,
 * account, table, room) is `noindex`. The Fiber fallback mirrors this with an
 * `X-Robots-Tag` header (api/server.go) so non-JS crawlers get the same answer.
 */

export const SITE_ORIGIN = 'https://app.bhabi.ca';
export const SITE_NAME = 'Bhabi';

export interface RouteSeo {
  readonly title: string;
  readonly description: string;
  readonly robots: string;
  /** Absolute canonical URL; null for non-indexable routes. */
  readonly canonical: string | null;
  readonly indexable: boolean;
  readonly jsonLd: Record<string, unknown> | null;
}

/** Public pages listed in public/sitemap.xml (kept in sync by a unit test). */
export const INDEXABLE_PATHS = ['/', '/game-rules', '/toc'] as const;

const INDEX = 'index, follow, max-image-preview:large';
const NOINDEX = 'noindex, follow';
const NOINDEX_PRIVATE = 'noindex, nofollow';

const DEFAULT_DESCRIPTION =
  'Play Bhabi, the classic Indian card game, free in your browser. Start a private table, share one link and play with friends, no sign-up needed.';

const HOME_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${SITE_ORIGIN}/#website`,
      name: SITE_NAME,
      alternateName: 'Bhabi Card Game',
      url: `${SITE_ORIGIN}/`,
      inLanguage: 'en',
    },
    {
      '@type': 'VideoGame',
      '@id': `${SITE_ORIGIN}/#game`,
      name: 'Bhabi Card Game',
      url: `${SITE_ORIGIN}/`,
      description: DEFAULT_DESCRIPTION,
      genre: ['Card game', 'Shedding game'],
      gamePlatform: ['Web browser', 'iOS'],
      applicationCategory: 'GameApplication',
      playMode: 'MultiPlayer',
      numberOfPlayers: { '@type': 'QuantitativeValue', minValue: 2, maxValue: 9 },
      isAccessibleForFree: true,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    },
  ],
};

const RULES_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_ORIGIN}/game-rules#page`,
  name: 'How to play Bhabi',
  url: `${SITE_ORIGIN}/game-rules`,
  inLanguage: 'en',
  isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
  about: { '@id': `${SITE_ORIGIN}/#game` },
};

const page = (
  path: string,
  title: string,
  description: string,
  jsonLd: Record<string, unknown> | null = null
): RouteSeo => ({
  title,
  description,
  robots: INDEX,
  canonical: `${SITE_ORIGIN}${path}`,
  indexable: true,
  jsonLd,
});

const hidden = (title: string, robots = NOINDEX): RouteSeo => ({
  title,
  description: DEFAULT_DESCRIPTION,
  robots,
  canonical: null,
  indexable: false,
  jsonLd: null,
});

/** Normalises a pathname: strips query/hash and trailing slashes. */
export const normalizePath = (pathname: string): string => {
  const p = (pathname || '/').split(/[?#]/)[0];
  const trimmed = p.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
};

export const seoForPath = (pathname: string): RouteSeo => {
  const path = normalizePath(pathname);
  switch (path) {
    case '/':
      return page(
        '/',
        'Bhabi card game – play free online with friends',
        DEFAULT_DESCRIPTION,
        HOME_JSON_LD
      );
    case '/game-rules':
      return page(
        '/game-rules',
        'How to play Bhabi – card game rules · Bhabi',
        'Bhabi card game rules: who leads with the ace of spades, following suit, what happens when a suit is broken, and why the last player holding cards is the Bhabi.',
        RULES_JSON_LD
      );
    case '/toc':
      return page(
        '/toc',
        'Terms of service & privacy policy · Bhabi',
        'Bhabi terms of service and privacy policy: accounts, guest play, coins, ads and how your data is handled.'
      );
    default:
      break;
  }
  if (/^\/join\/[^/]+$/.test(path)) {
    return hidden('Join a Bhabi table', NOINDEX_PRIVATE);
  }
  return hidden('Bhabi');
};
